import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckIcon, ChevronDownIcon, MapPinIcon, SearchIcon, ShoppingBagIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useSite } from "../context/SiteContext";
import { locations, type LocationId } from "../data/locations";
import { menus, sides } from "../data/menuData";
export function MenuPage({
  location: locationProp
}: {
  location?: LocationId;
}) {
  const {
    locationId
  } = useParams();
  const id: LocationId = locationProp ?? (locationId === "victor" ? "victor" : "driggs");
  const location = locations[id];
  const menu = menus[id];
  const {
    t,
    setSelectedLocation
  } = useSite();
  const [query, setQuery] = useState("");
  const [sidesOpen, setSidesOpen] = useState(false);
  useEffect(() => {
    setSelectedLocation(id);
    document.title = `${location.officialName} Menu | Agave Family`;
  }, [id, location.officialName, setSelectedLocation]);
  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return menu.map(category => ({
      ...category,
      items: category.items.filter(item => `${item.name} ${item.description}`.toLowerCase().includes(normalizedQuery))
    })).filter(category => category.items.length);
  }, [menu, query]);
  const format = (price: number) => `$${price.toFixed(2)}`;
  const categoryId = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return <>
      <section className="relative overflow-hidden bg-cocoa px-4 py-20 text-ivory sm:px-6 sm:py-28" data-id="element-471" data-mp="1el4vc3ck3n3s">
        <div className="relative mx-auto max-w-7xl" data-id="element-472" data-mp="1il5ajq7hc2bw">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cocoa/60 px-4 py-2 text-xs font-bold uppercase tracking-[.2em] text-gold backdrop-blur" data-id="element-473" data-mp="1nltax2hllvis">
            <CheckIcon className="h-4 w-4" data-id="element-474" data-mp="2z06v9xd57su2" />
            {t(`You are viewing the ${id === "driggs" ? "Driggs" : "Victor"} menu`, `Estás viendo el menú de ${id === "driggs" ? "Driggs" : "Victor"}`)}
          </p>
          <h1 className="max-w-4xl font-serif text-5xl font-semibold leading-none text-ivory sm:text-7xl lg:text-8xl" data-id="element-475" data-mp="uwlu3zroqx0t">
            {location.officialName}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-ivory/75" data-id="element-476" data-mp="13ylb4s2pa13r">
            {t("Authentic Mexican cuisine, prepared with traditional techniques and the warmth of family hospitality.", "Cocina mexicana auténtica, preparada con técnicas tradicionales y la calidez de la hospitalidad familiar.")}
          </p>
          <p className="mt-4 text-sm text-ivory/70" data-id="element-477" data-mp="l7250cddq6xi">
            {location.address} ·{" "}
            <a className="underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold" href={location.phoneHref} data-id="element-478" data-mp="3tmvon030byis">
              {location.phone}
            </a>
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-id="element-479" data-mp="3oly5ak69gyfp">
            <Link to={`/menus/${id === "driggs" ? "victor" : "driggs"}`} className="inline-flex min-h-12 items-center rounded-full border border-gold/40 px-6 text-sm font-bold transition-colors hover:bg-gold/10" data-id="element-480" data-mp="244yii6bvtdv3">
              {t(`Switch to ${id === "driggs" ? "Victor" : "Driggs"}`, `Cambiar a ${id === "driggs" ? "Victor" : "Driggs"}`)}
            </Link>
            <a href={location.directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center gap-2 rounded-full bg-gold px-6 text-sm font-bold text-cocoa transition-colors hover:bg-sand" data-id="element-481" data-mp="2rvcajwugvcpo">
              <MapPinIcon className="h-4 w-4" data-id="element-482" data-mp="12ge8k94qtpmh" />
              {t("Directions", "Cómo llegar")}
            </a>
          </div>
        </div>
      </section>

      <nav className="sticky top-[72px] z-30 border-b border-sand/50 bg-ivory/95 shadow-sm backdrop-blur" aria-label={t("Menu categories", "Categorías del menú")} data-id="element-483" data-mp="17g8ya9cs895y">
        <div className="mx-auto flex max-w-7xl gap-3 overflow-x-auto px-4 py-3 sm:px-6" data-id="element-484" data-mp="1hxw20op2l15e">
          <label className="relative min-w-[250px] flex-none" data-id="element-485" data-mp="1cz1kdq2u9imv">
            <SearchIcon className="absolute left-4 top-3 h-5 w-5 text-charcoal/45" data-id="element-486" data-mp="1czkuikm8z2qc" />
            <span className="sr-only" data-id="element-487" data-mp="18zao56ujmaay">{t("Search menu", "Buscar en el menú")}</span>
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder={t("Search dishes or ingredients", "Buscar platillos o ingredientes")} className="min-h-11 w-full rounded-full border border-sand bg-white py-2 pl-11 pr-4 text-sm text-charcoal placeholder:text-charcoal/45 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20" data-id="element-488" data-mp="25mxb8e5q8gbq" />
          </label>
          {menu.map(category => <a key={category.name} href={`#${categoryId(category.name)}`} className="inline-flex min-h-11 flex-none items-center rounded-full px-4 text-sm font-bold text-cocoa transition-colors hover:bg-terracotta/10 hover:text-terracotta" data-id="element-489" data-mp="g80n4ocr1z43">
              {category.name}
            </a>)}
        </div>
      </nav>

      <section className="bg-ivory px-4 py-16 sm:px-6 sm:py-24" data-id="element-490" data-mp="35oc1o40bsbje">
        <div className="mx-auto max-w-7xl" data-id="element-491" data-mp="35pn4o1ly5f9g">
          {filtered.length ? <div className="space-y-24" data-id="element-492" data-mp="3amn9yaaz2dzi">
              {filtered.map((category, categoryIndex) => <motion.section key={category.name} id={categoryId(category.name)} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.08
          }} transition={{
            duration: 0.65,
            delay: categoryIndex === 0 ? 0.1 : 0
          }} className="scroll-mt-44" data-id="element-493" data-mp="3fk5bhfmntqxh" data-mpi={categoryIndex}>
                  <div className="mb-10 rounded-[2rem] border border-gold/25 bg-cocoa p-8 sm:p-12" data-id="element-494" data-mp="2xjj7t1p3vmcz">
                    <p className="text-xs font-bold uppercase tracking-[.28em] text-gold" data-id="element-495" data-mp="2skpvvdpgw3ex">
                      {t("From our kitchen", "De nuestra cocina")}
                    </p>
                    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-id="element-496" data-mp="36hwd33ujlu21">
                      <h2 className="max-w-4xl font-serif text-5xl font-semibold leading-none text-ivory sm:text-7xl" data-id="element-497" data-mp="3gif54lw9ggv1">
                        {category.name}
                      </h2>
                      <span className="text-xs font-bold uppercase tracking-widest text-ivory/65" data-id="element-498" data-mp="3bh7tgbqzyxj3">
                        {category.items.length} {t("items", "platillos")}
                      </span>
                    </div>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-2" data-id="element-499" data-mp="34k4rjwoj21g">
                    {category.items.map((item, itemIndex) => <motion.article key={`${category.name}-${item.name}`} initial={{
                opacity: 0,
                y: 14
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                amount: 0.15
              }} transition={{
                duration: 0.45,
                delay: Math.min(itemIndex * 0.025, 0.18)
              }} className="group overflow-hidden rounded-2xl border border-sand/70 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold/60 hover:shadow-xl" data-id="element-500" data-mp="3ubmwccgfjac7" data-mpi={itemIndex}>
                        <div className="flex min-w-0 flex-col p-5 sm:p-6" data-id="element-501" data-mp="3rr8unj4r3pi">
                          <div className="flex items-start justify-between gap-4" data-id="element-502" data-mp="3l7zcm79zk4cp">
                            <h3 className="font-serif text-2xl font-semibold leading-tight text-cocoa" data-id="element-503" data-mp="3q59bsen6fudq">{item.name}</h3>
                            <p className="shrink-0 font-serif text-xl font-semibold text-terracotta" aria-label={`${item.name}, ${format(item.price)}`} data-id="element-504" data-mp="3c7kqj5p76l85">
                              {format(item.price)}
                            </p>
                          </div>
                          <p className="mt-3 text-sm leading-6 text-charcoal/70" data-id="element-505" data-mp="3mp6gro8ni9ck">{item.description}</p>
                        </div>
                      </motion.article>)}
                  </div>
                </motion.section>)}
            </div> : <div className="rounded-3xl border border-sand bg-white p-12 text-center" data-id="element-506" data-mp="38xrvfar9tw2c">
              <h2 className="font-serif text-3xl text-cocoa" data-id="element-507" data-mp="38vyk3i5pqno0">{t("No menu items found", "No encontramos platillos")}</h2>
              <button onClick={() => setQuery("")} className="mt-5 min-h-11 rounded-full bg-cocoa px-6 font-bold text-ivory transition-colors hover:bg-terracotta" data-id="element-508" data-mp="2yzvm8ohw9b6v">
                {t("Clear search", "Limpiar búsqueda")}
              </button>
            </div>}

          <section className="mt-24 overflow-hidden rounded-3xl border border-sand bg-white" data-id="element-509" data-mp="15ho7sbfi7o15">
            <button onClick={() => setSidesOpen(!sidesOpen)} className="flex min-h-20 w-full items-center justify-between px-6 text-left font-serif text-3xl font-semibold text-cocoa sm:px-8" aria-expanded={sidesOpen} data-id="element-510" data-mp="1af6l0e0k0pey">
              {t("Sides", "Acompañamientos")}
              <ChevronDownIcon className={`h-6 w-6 text-terracotta transition-transform ${sidesOpen ? "rotate-180" : ""}`} data-id="element-511" data-mp="mromlzmsat30" />
            </button>
            {sidesOpen && <div className="border-t border-sand p-6 sm:p-8" data-id="element-512" data-mp="vqjxhp0rwrcn">
                <div className="grid gap-x-10 md:grid-cols-2" data-id="element-513" data-mp="vp8onash0teo">
                  {sides.map(item => <article key={item.name} className="border-b border-sand/70 py-5" data-id="element-514" data-mp="32e9mtefam8l">
                      <div className="flex items-start justify-between gap-4" data-id="element-515" data-mp="30jxo4l4q4gn">
                        <h3 className="font-serif text-xl font-semibold text-cocoa" data-id="element-516" data-mp="7zo7mxfvo681">{item.name}</h3>
                        <strong className="font-serif text-lg text-terracotta" data-id="element-517" data-mp="3neq7xd01j2pz">{format(item.price)}</strong>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-charcoal/65" data-id="element-518" data-mp="ajqrnvofk83">{item.description}</p>
                    </article>)}
                </div>
              </div>}
          </section>

          <div className="mt-10 rounded-2xl border border-sand bg-warmwhite p-6 text-sm leading-6 text-charcoal/70" data-id="element-519" data-mp="33rs723arac0p">
            <p data-id="element-520" data-mp="38qvd9r2h2oa9">
              {t("Menu items, prices, and availability may change. Please confirm current availability when ordering.", "Los platillos, precios y disponibilidad pueden cambiar. Confirma la disponibilidad al realizar tu pedido.")}
            </p>
          </div>

          <div className="mt-10 flex flex-col items-center rounded-3xl bg-cocoa px-6 py-12 text-center text-ivory" data-id="element-521" data-mp="2l09765c13e2w">
            <ShoppingBagIcon className="h-9 w-9 text-gold" data-id="element-522" data-mp="2px9c2cx56asf" />
            <h2 className="mt-5 font-serif text-4xl" data-id="element-523" data-mp="2ierv5lmxrtpf">{t(`Order from ${location.shortName}`, `Ordena en ${location.shortName}`)}</h2>
            <p className="mt-3 max-w-xl text-ivory/70" data-id="element-524" data-mp="2df5lfm6bgvld">
              {t("Call the restaurant to place an order and confirm current availability.", "Llama al restaurante para ordenar y confirmar la disponibilidad actual.")}
            </p>
            <a href={location.phoneHref} className="mt-7 inline-flex min-h-12 items-center rounded-full bg-gold px-7 font-bold text-cocoa transition-colors hover:bg-sand" data-id="element-525" data-mp="1zjbl3rjlzejv">
              {t("Call", "Llamar")} {location.phone}
            </a>
          </div>
        </div>
      </section>
    </>;
}